import { useCallback, useEffect, useState } from 'react';
import { Title, useNotify } from 'react-admin';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { fmtDate } from '../../shared/Panel';
import { toErrorMessage } from '../userOffers/orderActionsApi';
import {
  EMPTY_THRESHOLD,
  fetchAccess,
  fetchAlertThresholds,
  fetchHistory,
  fetchMarginBands,
  fetchMarginThresholds,
  FEATURE_MARGIN_FIVE_BANDS,
  fetchPairScoreParameters,
  fetchParameters,
  fetchRegionDigits,
  formatParameterValue,
  parseParameterInput,
  saveParameter,
  CHANGE_OWNER,
  CHANGE_SCHEDULE_CANCELLED,
  FEATURE_PARAMETER_OWNERSHIP,
  ownerText,
  type ParameterChange,
  type SuggestionParameter,
} from './suggestionParametersApi';
import { fromDateTimeLocalValue } from '../../shared/clientTime';
import { OwnerDialog, CancelScheduleDialog } from './ParameterOwnershipDialogs';
import { menuLabel } from '../menuConfig';
import { StopTimesSection } from './StopTimesSection';
import { PriceStatisticsSection } from './PriceStatisticsSection';
import { FeatureGate, useFeature } from '../featureFlags/FeatureGate';
import { AddRegionCountryDialog } from './AddRegionCountryDialog';

/**
 * Parametry podpowiadania aut (zadanie 8 Etapu I, 28.09.2026).
 *
 * Stałe, które dotąd siedziały w kodzie — horyzont podpowiedzi, okno pozycji
 * z historii zleceń, wiek odczytu GPS i domyślna ładowność zestawu — jako
 * konfiguracja z historią zmian. Każda zmiana wymaga powodu i zostawia wpis:
 * kto, kiedy, z czego na co i dlaczego.
 *
 * Zapis wymaga uprawnienia sekcji administracyjnej. Czy zalogowany je ma,
 * mówi API (`/suggestion-parameters/access`, ta sama reguła co strażnik
 * zapisu) — bez niego ekran jest tylko do odczytu, z wyjaśnieniem, zamiast
 * dawać edycję kończącą się odmową 403 (30.09.2026). Nieznana odpowiedź
 * (ładowanie, błąd) to też odczyt: przycisku nie odblokowujemy na zgadywanie.
 * Rozstrzyga i tak API — wyszarzony przycisk nie jest uprawnieniem.
 * Zmiana dociera do podpowiedzi najpóźniej po minucie.
 *
 * Druga tabela to progi alertu marży (01.10.2026, odpowiedź Marathonu na
 * pytanie 12): wysoka / średnia / ±0 / wyraźnie ujemna, w % wartości
 * zlecenia. Wszystkie puste od wdrożenia — pusty próg = alert wyłączony, bo
 * liczb nie zgadujemy. Ładowana osobno: błąd jej odczytu nie zasłania
 * parametrów podpowiedzi.
 *
 * Pod nimi zestawienie czasów załadunku i rozładunku według miejsca
 * (`StopTimesSection`, 08.10.2026) — wartości mierzone, nie parametry,
 * więc bez edycji; administrator może je przeliczyć.
 */

/** Opis progów nad drugą tabelą — jak działa pasmo i że to nie ranking. */
export const MARGIN_NOTE =
  'Progi alertu marży w % wartości zlecenia: wysoka ≥ próg wysokiej; średnia ≥ próg średniej (poniżej wysokiej); ±0 — marża w granicach ± podanej liczby; wyraźnie ujemna ≤ próg (liczba ujemna). Pusty próg = alert wyłączony. Alert stoi przy marży w podpowiedziach pojazdów — nie zmienia kolejności i niczego nie blokuje.';

/**
 * Opis nad tabelą pięciu pasm marży (paczka p. Mariusza 21.10, S-21.10-18) —
 * zastępuje progi alertu, gdy przełącznik `marginFiveBands` jest włączony.
 */
export const MARGIN_BANDS_NOTE =
  'Pięć pasm marży w % kosztu: poniżej pierwszej granicy — strata krytyczna, do drugiej — strata, do trzeciej — marża minimalna, do czwartej włącznie — marża normalna, powyżej — do weryfikacji. W Trade koszt to oferta przewoźnika (koszt frachtu), w rekomendacjach pojazdów — koszt własny (dolot i trasa). Wartości robocze z paczki p. Mariusza z 21.10: −50 / 0 / 10 / 70. Pasmo to tylko oznaczenie przy marży — nie zmienia kolejności i niczego nie blokuje.';

/** Opis nad tabelą oceny pary (13.10.2026) — jak liczona i czego nie robi. */
export const PAIR_SCORE_NOTE =
  'Ocena pary zlecenie–pojazd 0–10 przy rekomendacjach: marża (w % wartości zlecenia), wykonalność czasowa, dolot (w % trasy zlecenia) i priorytet klienta, każda 0–10 z wagą. Wagi są względne — składowa bez danych wypada, a pozostałe wagi przeliczamy. Progi marży i dolotu ustawiliśmy testem wstecznym na historii obsadzeń (13.10.2026); marża liczona z opłatami drogowymi. Ocena nie zmienia kolejności rekomendacji (ta zostaje po dolocie) i nikogo nie wyklucza.';

/**
 * Opis nad tabelą progów alertów i założeń roboczych (15.10.2026, Etap 1
 * makiety FTL, PROMPT pkt 15: „wartość robocza, do potwierdzenia”).
 */
export const ALERT_THRESHOLDS_NOTE =
  'Nasze założenia, dotąd stałe w kodzie — wartości robocze, do potwierdzenia przez Marathon. Wartość domyślna to dotychczasowa liczba, więc bez zmiany działa wszystko jak dotąd. Zmiana działa najpóźniej po minucie w alercie o przeplanowaniu, w odczycie osi czasu z tachografu, w ostrzeżeniu o podejrzanej dacie z TMS i w liczbie zajętych pojazdów pokazywanych na końcu listy rekomendacji.';

/**
 * Opis nad tabelą regionu per kraj (21.10.2026, `regionDigitsByCountry`;
 * weryfikacja B13 i decyzja użytkownika 21.10.2026).
 */
export const REGION_DIGITS_NOTE =
  '„Mój region” na pulpicie planisty to kraj i pierwsze cyfry kodu pocztowego miejsc, w których zwalniają się jego pojazdy. Ile cyfr — według kraju: w dużych krajach 2 cyfry to okolica ok. 35–60 km, w małych (NL, BE, CH, DK) tę samą skalę daje 1 cyfra. Pusta wartość kraju = jak w krajach bez własnej wartości. Wartości robocze, do potwierdzenia przez Marathon.';

/** Wartość parametru do pokazania — pusty próg opcjonalny to nie myślnik. */
const showValue = (
  v: number | null | undefined,
  p?: Pick<SuggestionParameter, 'unit' | 'optional' | 'zeroMeans'>,
) =>
  (v === null || v === undefined) && p?.optional
    ? EMPTY_THRESHOLD
    : formatParameterValue(v, { unit: p?.unit ?? '', zeroMeans: p?.zeroMeans });

/**
 * Opis nad listą przy przełączniku `parameterOwnership` (24.10.2026,
 * S-24.10-12; wymagania przekrojowe §1).
 */
export const OWNERSHIP_NOTE =
  'Każdy parametr ma właściciela biznesowego i datę obowiązywania. Zmianę można zaplanować — nowa wartość z datą „obowiązuje od” niczego nie zmienia przed tą datą, a do tego czasu można ją odwołać z powodem. Historia pokazuje: było, jest, od kiedy, kto i dlaczego.';

/** Zmiana w historii słowami — wartość, właściciel albo odwołanie. */
export function changeText(
  c: ParameterChange,
  p?: Pick<SuggestionParameter, 'unit' | 'optional' | 'zeroMeans'>,
): string {
  if (c.field === CHANGE_OWNER) {
    return `Właściciel: ${ownerText(c.oldText)} → ${ownerText(c.newText)}`;
  }
  if (c.field === CHANGE_SCHEDULE_CANCELLED) {
    return `Odwołana zmiana na ${showValue(c.oldValue, p)} — zostaje ${showValue(c.newValue, p)}`;
  }
  return `${showValue(c.oldValue, p)} → ${showValue(c.newValue, p)}`;
}

/** Komunikat dla osoby bez uprawnienia do zmiany. */
export const READ_ONLY_NOTE =
  'Zmiana parametrów wymaga uprawnienia sekcji administracyjnej — masz dostęp tylko do odczytu.';
export const SuggestionParameterList = () => {
  const notify = useNotify();
  const [parameters, setParameters] = useState<SuggestionParameter[] | null>(null);
  const [margin, setMargin] = useState<SuggestionParameter[] | null>(null);
  const [marginError, setMarginError] = useState<string | null>(null);
  // Ocena pary 0–10 (13.10.2026) — tylko z przełącznikiem `pairScoring`.
  const scoring = useFeature('pairScoring');
  const [pairScore, setPairScore] = useState<SuggestionParameter[] | null>(null);
  const [pairScoreError, setPairScoreError] = useState<string | null>(null);
  // Progi alertów i założenia robocze (15.10.2026) — tylko z przełącznikiem
  // `alertThresholdParameters`.
  const alertsOn = useFeature('alertThresholdParameters');
  // Pięć pasm marży (21.10.2026, S-21.10-18) — zamiast czterech progów.
  const bandsOn = useFeature(FEATURE_MARGIN_FIVE_BANDS);
  const [alerts, setAlerts] = useState<SuggestionParameter[] | null>(null);
  const [alertsError, setAlertsError] = useState<string | null>(null);
  // Region na pulpitach FTL per kraj (21.10.2026) — z przełącznikiem
  // `regionDigitsByCountry` i pulpitem planisty.
  const regionByCountry = useFeature('regionDigitsByCountry');
  const plannerDashboardOn = useFeature('plannerDashboard');
  const regionOn = regionByCountry && plannerDashboardOn;
  const [region, setRegion] = useState<SuggestionParameter[] | null>(null);
  const [regionError, setRegionError] = useState<string | null>(null);
  const [addingCountry, setAddingCountry] = useState(false);
  const [history, setHistory] = useState<ParameterChange[]>([]);
  const [historyKey, setHistoryKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<SuggestionParameter | null>(null);
  // `null` = jeszcze nie wiemy; do czasu odpowiedzi edycja zamknięta.
  const [canEdit, setCanEdit] = useState<boolean | null>(null);
  // Właściciel i data obowiązywania (24.10.2026, S-24.10-12).
  const ownershipOn = useFeature(FEATURE_PARAMETER_OWNERSHIP);
  const [owning, setOwning] = useState<SuggestionParameter | null>(null);
  const [cancelling, setCancelling] = useState<SuggestionParameter | null>(null);

  useEffect(() => {
    let active = true;
    fetchAccess()
      .then((a) => {
        if (active) setCanEdit(a.canEdit === true);
      })
      .catch(() => {
        // Nie wiemy, czy wolno — zostaje odczyt. Parametry i tak się pokażą.
        if (active) setCanEdit(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const load = useCallback(async () => {
    try {
      const [p, h] = await Promise.all([fetchParameters(), fetchHistory(historyKey)]);
      setParameters(p);
      setHistory(h);
      setError(null);
    } catch (e) {
      setError(toErrorMessage(e));
    }
  }, [historyKey]);

  const loadMargin = useCallback(async () => {
    try {
      setMargin(await (bandsOn ? fetchMarginBands() : fetchMarginThresholds()));
      setMarginError(null);
    } catch (e) {
      setMarginError(toErrorMessage(e));
    }
  }, [bandsOn]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    void loadMargin();
  }, [loadMargin]);

  const loadPairScore = useCallback(async () => {
    if (!scoring) return;
    try {
      setPairScore(await fetchPairScoreParameters());
      setPairScoreError(null);
    } catch (e) {
      setPairScoreError(toErrorMessage(e));
    }
  }, [scoring]);

  useEffect(() => {
    void loadPairScore();
  }, [loadPairScore]);

  const loadAlerts = useCallback(async () => {
    if (!alertsOn) return;
    try {
      setAlerts(await fetchAlertThresholds());
      setAlertsError(null);
    } catch (e) {
      setAlertsError(toErrorMessage(e));
    }
  }, [alertsOn]);

  useEffect(() => {
    void loadAlerts();
  }, [loadAlerts]);

  const loadRegion = useCallback(async () => {
    if (!regionOn) return;
    try {
      setRegion(await fetchRegionDigits());
      setRegionError(null);
    } catch (e) {
      setRegionError(toErrorMessage(e));
    }
  }, [regionOn]);

  useEffect(() => {
    void loadRegion();
  }, [loadRegion]);

  const all = [
    ...(parameters ?? []),
    ...(margin ?? []),
    ...(scoring ? (pairScore ?? []) : []),
    ...(alertsOn ? (alerts ?? []) : []),
    ...(regionOn ? (region ?? []) : []),
  ];
  const paramOf = (key: string) => all.find((p) => p.key === key);
  const labelOf = (key: string) => paramOf(key)?.label ?? key;

  return (
    <Box sx={{ p: 2 }}>
      {/* Tytuł w niebieskim pasku, jak na ekranach z `<List>` — nagłówek
          w treści zostawiał pasek pusty (e2e na dev, 28.09.2026). */}
      <Title title={menuLabel('suggestion-parameters')} />
      <Alert severity="info" sx={{ mb: 2 }}>
        Wartości to nasze założenia, nie liczby od klienta — każda zmiana wymaga powodu i trafia
        do historii poniżej. Rekomendacje uwzględniają nową wartość najpóźniej po minucie.
      </Alert>
      {ownershipOn && (
        <Alert severity="info" sx={{ mb: 2 }} data-testid="parameter-ownership-note">
          {OWNERSHIP_NOTE}
        </Alert>
      )}
      {canEdit === false && (
        <Alert severity="warning" sx={{ mb: 2 }} data-testid="read-only-note">
          {READ_ONLY_NOTE}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {parameters === null && !error ? (
        <CircularProgress size={24} />
      ) : (
        <ParametersTable
          name={menuLabel('suggestion-parameters')}
          rows={parameters ?? []}
          canEdit={canEdit}
          onEdit={setEditing}
          onHistory={setHistoryKey}
          ownership={ownershipOn}
          onOwner={setOwning}
          onCancelSchedule={setCancelling}
        />
      )}

      <Typography variant="h6" sx={{ mt: 4, mb: 1 }}>
        {bandsOn ? 'Pasma marży' : 'Progi alertu marży'}
      </Typography>
      <Alert severity="info" sx={{ mb: 2 }} data-testid={bandsOn ? 'margin-bands-note' : 'margin-note'}>
        {bandsOn ? MARGIN_BANDS_NOTE : MARGIN_NOTE}
      </Alert>
      {marginError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          Nie udało się wczytać {bandsOn ? 'granic pasm' : 'progów'} marży: {marginError}
        </Alert>
      ) : margin === null ? (
        <CircularProgress size={24} />
      ) : (
        <ParametersTable
          name={bandsOn ? 'Pasma marży' : 'Progi alertu marży'}
          rows={margin}
          canEdit={canEdit}
          onEdit={setEditing}
          onHistory={setHistoryKey}
          ownership={ownershipOn}
          onOwner={setOwning}
          onCancelSchedule={setCancelling}
        />
      )}

      {/* Wagi i progi oceny pary 0–10 (13.10.2026) — za przełącznikiem
          `pairScoring`; w wersji „obecnej” sekcji nie ma. */}
      {scoring && (
        <>
          <Typography variant="h6" sx={{ mt: 4, mb: 1 }}>
            Ocena pary zlecenie–pojazd (0–10)
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }} data-testid="pair-score-note">
            {PAIR_SCORE_NOTE}
          </Alert>
          {pairScoreError ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              Nie udało się wczytać parametrów oceny pary: {pairScoreError}
            </Alert>
          ) : pairScore === null ? (
            <CircularProgress size={24} />
          ) : (
            <ParametersTable
              name="Ocena pary zlecenie–pojazd"
              rows={pairScore}
              canEdit={canEdit}
              onEdit={setEditing}
              onHistory={setHistoryKey}
              ownership={ownershipOn}
              onOwner={setOwning}
              onCancelSchedule={setCancelling}
            />
          )}
        </>
      )}

      {/* Progi alertów i założenia robocze (15.10.2026, Etap 1 makiety FTL)
          — za przełącznikiem `alertThresholdParameters`; w wersji „obecnej”
          sekcji nie ma, a reguły liczą stałymi z kodu. */}
      {alertsOn && (
        <>
          <Typography variant="h6" sx={{ mt: 4, mb: 1 }}>
            Progi alertów i założenia robocze
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }} data-testid="alert-thresholds-note">
            {ALERT_THRESHOLDS_NOTE}
          </Alert>
          {alertsError ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              Nie udało się wczytać progów alertów: {alertsError}
            </Alert>
          ) : alerts === null ? (
            <CircularProgress size={24} />
          ) : (
            <ParametersTable
              name="Progi alertów i założenia robocze"
              rows={alerts}
              canEdit={canEdit}
              onEdit={setEditing}
              onHistory={setHistoryKey}
              ownership={ownershipOn}
              onOwner={setOwning}
              onCancelSchedule={setCancelling}
            />
          )}
        </>
      )}

      {/* Region na pulpitach FTL per kraj (21.10.2026, S-21.10-25) — za
          przełącznikiem `regionDigitsByCountry`; nowy kraj z powodem. */}
      {regionOn && (
        <>
          <Stack direction="row" spacing={2} sx={{ mt: 4, mb: 1, alignItems: 'center' }}>
            <Typography variant="h6">Region na pulpitach FTL</Typography>
            <Button
              size="small"
              disabled={canEdit !== true}
              onClick={() => setAddingCountry(true)}
              data-testid="region-digits-add-country"
            >
              Dodaj kraj
            </Button>
          </Stack>
          <Alert severity="info" sx={{ mb: 2 }} data-testid="region-digits-note">
            {REGION_DIGITS_NOTE}
          </Alert>
          {regionError ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              Nie udało się wczytać regionu na pulpitach: {regionError}
            </Alert>
          ) : region === null ? (
            <CircularProgress size={24} />
          ) : (
            <ParametersTable
              name="Region na pulpitach FTL"
              rows={region}
              canEdit={canEdit}
              onEdit={setEditing}
              onHistory={setHistoryKey}
              ownership={ownershipOn}
              onOwner={setOwning}
              onCancelSchedule={setCancelling}
            />
          )}
        </>
      )}

      {/* Czasy w punktach według miejsca (08.10.2026) — własne zapytania;
          błąd ich odczytu nie zasłania parametrów. */}
      {/* Za przełącznikiem `stopTimesByPlace` (12.10.2026) — w wersji
          „obecnej” sekcji nie ma, jak w 0.31.3. */}
      <FeatureGate feature="stopTimesByPlace">
        <StopTimesSection canEdit={canEdit} />
      </FeatureGate>

      {/* Statystyka cen z VIA (13.10.2026) — stan i „Przelicz teraz”; za
          przełącznikiem `priceStatistics`, w wersji „obecnej” sekcji nie ma. */}
      <FeatureGate feature="priceStatistics">
        <PriceStatisticsSection canEdit={canEdit} />
      </FeatureGate>

      <Stack direction="row" spacing={2} sx={{ mt: 4, mb: 1, alignItems: 'center' }}>
        <Typography variant="h6">
          Historia zmian{historyKey ? ` — ${labelOf(historyKey)}` : ''}
        </Typography>
        {historyKey && (
          <Button size="small" onClick={() => setHistoryKey(null)}>
            Wszystkie parametry
          </Button>
        )}
      </Stack>
      {history.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Brak zmian — obowiązują wartości z wdrożenia.
        </Typography>
      ) : (
        <Table size="small" aria-label="Historia zmian">
          <TableHead>
            <TableRow>
              <TableCell>Kiedy</TableCell>
              <TableCell>Kto</TableCell>
              <TableCell>Parametr</TableCell>
              <TableCell>Zmiana</TableCell>
              {ownershipOn && <TableCell>Obowiązuje od</TableCell>}
              <TableCell>Powód</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {history.map((c) => (
              <TableRow key={c.id}>
                <TableCell>{fmtDate(c.changedAt)}</TableCell>
                <TableCell>{c.changedByName ?? '—'}</TableCell>
                <TableCell>{labelOf(c.key)}</TableCell>
                <TableCell>{changeText(c, paramOf(c.key))}</TableCell>
                {ownershipOn && (
                  <TableCell>
                    {c.field === CHANGE_OWNER ? '—' : fmtDate(c.validFrom ?? c.changedAt)}
                  </TableCell>
                )}
                <TableCell>{c.reason}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {editing && canEdit === true && (
        <EditParameterDialog
          parameter={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            notify('Parametr zapisany', { type: 'success' });
            void load();
            void loadMargin();
            void loadPairScore();
            void loadAlerts();
            void loadRegion();
          }}
          scheduling={ownershipOn}
        />
      )}
      {owning && canEdit === true && (
        <OwnerDialog
          parameter={owning}
          onClose={() => setOwning(null)}
          onSaved={() => {
            setOwning(null);
            notify('Właściciel zapisany', { type: 'success' });
            void load();
            void loadMargin();
            void loadPairScore();
            void loadAlerts();
            void loadRegion();
          }}
        />
      )}
      {cancelling && canEdit === true && (
        <CancelScheduleDialog
          parameter={cancelling}
          onClose={() => setCancelling(null)}
          onSaved={() => {
            setCancelling(null);
            notify('Zmiana zaplanowana odwołana', { type: 'success' });
            void load();
            void loadMargin();
            void loadPairScore();
            void loadAlerts();
            void loadRegion();
          }}
        />
      )}
      {addingCountry && canEdit === true && (
        <AddRegionCountryDialog
          existing={(region ?? []).map((p) => p.key)}
          onClose={() => setAddingCountry(false)}
          onSaved={() => {
            setAddingCountry(false);
            notify('Parametr zapisany', { type: 'success' });
            void loadRegion();
            void load();
          }}
        />
      )}
    </Box>
  );
};

/** Tabela parametrów — ta sama dla parametrów podpowiedzi i progów marży. */
function ParametersTable({
  name,
  rows,
  canEdit,
  onEdit,
  onHistory,
  ownership = false,
  onOwner,
  onCancelSchedule,
}: {
  name: string;
  rows: SuggestionParameter[];
  canEdit: boolean | null;
  onEdit: (p: SuggestionParameter) => void;
  onHistory: (key: string) => void;
  /** Kolumna właściciela i obowiązywania (S-24.10-12). */
  ownership?: boolean;
  onOwner?: (p: SuggestionParameter) => void;
  onCancelSchedule?: (p: SuggestionParameter) => void;
}) {
  return (
    <Table size="small" aria-label={name}>
      <TableHead>
        <TableRow>
          <TableCell>Parametr</TableCell>
          <TableCell>Wartość</TableCell>
          <TableCell>Zakres</TableCell>
          {ownership && <TableCell>Właściciel i obowiązywanie</TableCell>}
          <TableCell>Ostatnia zmiana</TableCell>
          <TableCell />
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((p) => (
          <TableRow key={p.key} data-testid={`suggestion-parameter-${p.key}`}>
            <TableCell sx={{ maxWidth: 420 }}>
              {/* Klucz techniczny (np. `planningHorizonDays`) tylko w dymku —
                  pod nazwą nic operatorowi nie mówił (lista komunikatów
                  z 01.10.2026). Potrzebny przy zmianie przez API. */}
              <Tooltip title={`Klucz techniczny: ${p.key}`} describeChild>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {p.label}
                </Typography>
              </Tooltip>
              {p.description && (
                <Typography variant="caption" color="text.secondary" component="div">
                  {p.description}
                </Typography>
              )}
            </TableCell>
            <TableCell>
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                <span>{showValue(p.value, p)}</span>
                {!p.configured && (
                  <Chip
                    size="small"
                    color="warning"
                    variant="outlined"
                    label={p.optional ? 'wartość spoza zakresu' : 'domyślna z kodu'}
                  />
                )}
              </Stack>
              {p.defaultValue !== null && (
                <Typography variant="caption" color="text.secondary">
                  domyślnie {formatParameterValue(p.defaultValue, p)}
                </Typography>
              )}
            </TableCell>
            <TableCell>
              {p.min}–{p.max} {p.unit}
              {p.zeroMeans ? ` albo 0 (${p.zeroMeans})` : ''}
            </TableCell>
            {ownership && (
              <TableCell sx={{ minWidth: 200 }}>
                <OwnershipCell
                  p={p}
                  canEdit={canEdit}
                  onOwner={onOwner}
                  onCancelSchedule={onCancelSchedule}
                />
              </TableCell>
            )}
            <TableCell>
              {p.lastChange ? (
                <>
                  <Typography variant="body2">
                    {fmtDate(p.lastChange.changedAt)} · {p.lastChange.changedByName ?? '—'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {p.lastChange.reason}
                  </Typography>
                </>
              ) : (
                <Typography variant="caption" color="text.secondary">
                  bez zmian od wdrożenia
                </Typography>
              )}
            </TableCell>
            <TableCell align="right">
              <Stack direction="row" spacing={1}>
                <Button size="small" disabled={canEdit !== true} onClick={() => onEdit(p)}>
                  Zmień
                </Button>
                <Button size="small" onClick={() => onHistory(p.key)}>
                  Historia
                </Button>
              </Stack>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

/**
 * Właściciel, od kiedy obowiązuje wartość i zmiana zaplanowana (24.10.2026,
 * S-24.10-12) — z przyciskami „Właściciel” i „Odwołaj”.
 */
function OwnershipCell({
  p,
  canEdit,
  onOwner,
  onCancelSchedule,
}: {
  p: SuggestionParameter;
  canEdit: boolean | null;
  onOwner?: (p: SuggestionParameter) => void;
  onCancelSchedule?: (p: SuggestionParameter) => void;
}) {
  return (
    <Stack spacing={0.5} sx={{ alignItems: 'flex-start' }}>
      <Typography
        variant="body2"
        color={p.owner ? 'text.primary' : 'text.secondary'}
        data-testid={`parameter-owner-${p.key}`}
      >
        {ownerText(p.owner)}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {p.validFrom ? `obowiązuje od ${fmtDate(p.validFrom)}` : 'obowiązuje od wdrożenia'}
      </Typography>
      {p.scheduled && (
        <Tooltip
          title={`Zaplanował(a): ${p.scheduled.byName ?? '—'}${p.scheduled.reason ? ` · ${p.scheduled.reason}` : ''}`}
          describeChild
        >
          <Chip
            size="small"
            color="info"
            variant="outlined"
            data-testid={`parameter-scheduled-${p.key}`}
            label={`od ${fmtDate(p.scheduled.validFrom)}: ${showValue(p.scheduled.value, p)}`}
          />
        </Tooltip>
      )}
      <Stack direction="row" spacing={1}>
        <Button
          size="small"
          disabled={canEdit !== true}
          onClick={() => onOwner?.(p)}
          data-testid={`parameter-owner-edit-${p.key}`}
        >
          Właściciel
        </Button>
        {p.scheduled && (
          <Button
            size="small"
            color="warning"
            disabled={canEdit !== true}
            onClick={() => onCancelSchedule?.(p)}
            data-testid={`parameter-cancel-schedule-${p.key}`}
          >
            Odwołaj
          </Button>
        )}
      </Stack>
    </Stack>
  );
}

function EditParameterDialog({
  parameter,
  onClose,
  onSaved,
  scheduling = false,
}: {
  parameter: SuggestionParameter;
  onClose: () => void;
  onSaved: () => void;
  /** Pole „Obowiązuje od” (S-24.10-12). */
  scheduling?: boolean;
}) {
  const [value, setValue] = useState(parameter.value === null ? '' : String(parameter.value));
  const [reason, setReason] = useState('');
  // Puste = od razu (czas polski, jak w całej aplikacji).
  const [validFrom, setValidFrom] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    const parsed = parseParameterInput(value, parameter);
    if ('error' in parsed) {
      setError(parsed.error);
      return;
    }
    if (reason.trim() === '') {
      setError('Podaj powód zmiany');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await saveParameter(
        parameter.key,
        parsed.value,
        reason.trim(),
        scheduling ? fromDateTimeLocalValue(validFrom) : null,
      );
      onSaved();
    } catch (e) {
      setError(toErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{parameter.label}</DialogTitle>
      <DialogContent>
        {parameter.description && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {parameter.description}
          </Typography>
        )}
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label={`Wartość (${parameter.unit}), zakres ${parameter.min}–${parameter.max}`}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            size="small"
            helperText={
              parameter.optional
                ? 'Puste pole = próg wyczyszczony, alert wyłączony.'
                : parameter.zeroMeans
                  ? `0 = ${parameter.zeroMeans}.`
                  : undefined
            }
            slotProps={{
              // Klawiatura liczbowa na telefonie nie ma minusa — przy
              // progach ujemnych zwykła.
              htmlInput: {
                inputMode:
                  parameter.min < 0 ? 'text' : parameter.decimals ? 'decimal' : 'numeric',
              },
            }}
          />
          {scheduling && (
            <TextField
              type="datetime-local"
              label="Obowiązuje od (czas polski)"
              value={validFrom}
              onChange={(e) => setValidFrom(e.target.value)}
              size="small"
              helperText="Puste pole — zmiana działa od razu. Data w przyszłości — zmiana zaplanowana, do tego czasu obowiązuje obecna wartość."
              slotProps={{
                inputLabel: { shrink: true },
                htmlInput: { 'data-testid': 'parameter-valid-from' },
              }}
            />
          )}
          <TextField
            label="Powód zmiany"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            size="small"
            multiline
            minRows={2}
            required
            slotProps={{ htmlInput: { maxLength: 500 } }}
          />
        </Stack>
        {error && (
          <Typography role="alert" color="error" variant="body2" sx={{ mt: 2 }}>
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Anuluj
        </Button>
        <Button onClick={save} variant="contained" disabled={busy}>
          Zapisz
        </Button>
      </DialogActions>
    </Dialog>
  );
}
